import React, { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { preofferService } from '../services/preofferService';
import './Dashboard.css';

const Dashboard = () => {
  const [preoffers, setPreoffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPreoffer, setSelectedPreoffer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Récupérer l'utilisateur depuis le localStorage
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch (err) {
      console.error('Erreur récupération user:', err);
      return null;
    }
  };

  const currentUser = getCurrentUser();
  const currentCompany = currentUser ? {
    _id: currentUser.companyId,
    name: currentUser.companyName
  } : null;

  // Catégories des pré-offres
  const CATEGORIES = [
    { id: 'all', name: 'Toutes les catégories', icon: '🌐' },
    { id: 'financial', name: 'Support financier', icon: '💰' },
    { id: 'family', name: 'Famille & éducation', icon: '👨‍👩‍👧‍👦' },
    { id: 'shopping', name: 'Achats courants', icon: '🛍️' },
    { id: 'leisure', name: 'Voyages & loisirs', icon: '🌴' },
    { id: 'health', name: 'Santé', icon: '🏥' },
    { id: 'food', name: 'Restauration', icon: '🍽️' },
    { id: 'religious', name: 'Vie spirituelle', icon: '🕋' },
    { id: 'wellness', name: 'Sport & bien-être', icon: '🧘' },
    { id: 'training', name: 'Formation pro', icon: '💼' },
    { id: 'culture', name: 'Culture', icon: '🎭' }
  ];

  // Charger les pré-offres depuis l'API
  const fetchPreoffers = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔄 Chargement des pré-offres depuis API...');
      const data = await preofferService.getActivePreoffers({
        category: selectedCategory !== 'all' ? selectedCategory : null
      });
      
      console.log('✅ Pré-offres chargées:', data);
      setPreoffers(data || []);
      
    } catch (err) {
      const errorMessage = err.message || 'Erreur lors du chargement des propositions';
      setError(errorMessage);
      console.error('❌ Erreur fetch preoffers:', err);
      setPreoffers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreoffers();
  }, [selectedCategory]);

  // Ouvrir modal de détails
  const handleViewDetails = (preoffer) => {
    setSelectedPreoffer(preoffer);
    setShowModal(true);
  };

  // Fermer modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPreoffer(null);
  };

  // Exprimer son intérêt via l'API
  const handleExpressInterest = async (preoffer) => {
    if (!currentCompany) {
      setError('Veuillez vous connecter pour exprimer votre intérêt');
      return;
    }

    if (!window.confirm(`Êtes-vous intéressé par "${preoffer.title}" ?\n\nNous vous contacterons pour plus de détails.`)) {
      return;
    }

    try {
      console.log('🎯 Expression d\'intérêt pour:', preoffer.title);
      console.log('🏢 Entreprise:', currentCompany);
      
      await preofferService.expressInterest(preoffer._id, {
        companyId: currentCompany._id,
        notes: 'Intérêt exprimé via le dashboard'
      });
      
      await fetchPreoffers();
      
      alert('✅ Merci ! Votre intérêt a été enregistré. Nous vous contacterons rapidement.');
    } catch (err) {
      const errorMessage = err.message || 'Erreur lors de l\'enregistrement de votre intérêt';
      setError(errorMessage);
      console.error('❌ Erreur express interest:', err);
    }
  };

  // Vérifier si l'entreprise courante est déjà intéressée
  const isCompanyInterested = (preoffer) => {
    if (!currentCompany || !preoffer.interestedCompanies) return false;
    return preoffer.interestedCompanies.some(
      company => company.companyId === currentCompany._id
    );
  };

  // Obtenir l'icône de catégorie
  const getCategoryIcon = (categoryId) => {
    return CATEGORIES.find(c => c.id === categoryId)?.icon || '📌';
  };

  const getCategoryName = (categoryId) => {
    return CATEGORIES.find(c => c.id === categoryId)?.name || categoryId;
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="page-header">
          <div className="page-title">
            <h1>Actualités & Propositions</h1>
            <p>Chargement...</p>
          </div>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Chargement des actualités...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* En-tête */}
      <div className="page-header">
        <div className="page-title">
          <h1>Actualités & Propositions</h1>
          <p>Découvrez les nouvelles offres que nous préparons</p>
        </div>
        
        {/* Filtres */}
        <div className="header-actions">
          <div className="filters">
            <select 
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="filter-select"
            >
              {CATEGORIES.map(category => (
                <option key={category.id} value={category.id}>
                  {category.icon} {category.name}
                </option>
              ))}
            </select>

            <button 
              className="filter-reset"
              onClick={fetchPreoffers}
            >
              🔄 Actualiser
            </button>
          </div>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <Card className="error-card">
          <div className="error-message">
            <span>❌</span>
            <div className="error-content">
              <strong>Erreur</strong>
              <p>{error}</p>
              <button 
                onClick={fetchPreoffers}
                className="retry-button"
              >
                🔄 Réessayer
              </button>
            </div>
            <button onClick={() => setError('')} className="error-close">✕</button>
          </div>
        </Card>
      )}



<div className="dashboard-table-container">
  <table className="dashboard-table">
    <thead>
      <tr>
        <th>Catégorie</th>
        <th>Titre & Description</th>
        <th>Partenaire</th>
        <th>Avantage</th>
        <th>Intérêt</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      {preoffers.map(preoffer => {
        const isInterested = isCompanyInterested(preoffer);
        const interestedCount = preoffer.interestedCompanies?.length || 0;
        const hasConditions = preoffer.conditions && preoffer.conditions.length > 0 && preoffer.conditions[0] !== '';
        const isConverted = preoffer.convertedToOffer === true;
        
        // Tronquer la description (100 caractères max)
        const shortDescription = preoffer.description.length > 100 
          ? preoffer.description.substring(0, 100) + '...' 
          : preoffer.description;
        
        return (
          <tr key={preoffer._id}>
            {/* Catégorie */}
            <td data-label="Catégorie">
              <div className="category-cell">
                <span className="category-badge">
                  {getCategoryIcon(preoffer.category)} {getCategoryName(preoffer.category)}
                </span>
                {isConverted && (
                  <span className="converted-badge">✅ Offre active</span>
                )}
              </div>
            </td>
            
            {/* Titre & Description - version tronquée */}
            <td data-label="Titre & Description">
              <div className="offer-cell">
                <strong>{preoffer.title}</strong>
                <p className="offer-desc-preview">{shortDescription}</p>
                {hasConditions && (
                  <div className="has-conditions">
                    <span className="conditions-indicator">📋 Conditions disponibles</span>
                  </div>
                )}
              </div>
            </td>
            
            {/* Partenaire */}
            <td data-label="Partenaire">
              {preoffer.partnerName ? (
                <span className="partner-name">{preoffer.partnerName}</span>
              ) : (
                <span className="empty-value">—</span>
              )}
            </td>
            
            {/* Avantage */}
            <td data-label="Avantage">
              {preoffer.discount ? (
                <span className="discount-value">{preoffer.discount}</span>
              ) : (
                <span className="empty-value">—</span>
              )}
            </td>
            
            {/* Intérêt */}
            <td data-label="Intérêt">
              <div className="interest-cell">
                <span className="interest-count">{interestedCount}</span>
                <span className="interest-label">entreprise{interestedCount > 1 ? 's' : ''}</span>
                {isInterested && (
                  <span className="interested-badge">✓ Vous</span>
                )}
              </div>
            </td>
            
            {/* Actions - Boutons plus grands et lisibles */}
            <td data-label="Actions" className="actions-cell">
              <button 
                className="action-btn view"
                onClick={() => handleViewDetails(preoffer)}
                title="Voir les détails complets"
              >
                👁️ Voir détails
              </button>
              
              {!isInterested ? (
                <button 
                  className="action-btn interest"
                  onClick={() => handleExpressInterest(preoffer)}
                  disabled={!currentCompany}
                  title="Exprimer mon intérêt"
                >
                  👍 Je suis intéressé
                </button>
              ) : (
                <span className="action-confirmed" title="Intérêt déjà exprimé">
                  ✅ Intérêt enregistré
                </span>
              )}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
</div>

      {/* État vide */}
      {preoffers.length === 0 && !loading && (
        <Card className="empty-state">
          <div className="empty-icon">💡</div>
          <h3 className="empty-title">Aucune actualité pour le moment</h3>
          <p className="empty-text">Revenez plus tard pour découvrir de nouvelles propositions</p>
        </Card>
      )}

      {/* MODAL DE DÉTAILS */}
      {showModal && selectedPreoffer && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <span>{getCategoryIcon(selectedPreoffer.category)}</span>
                {selectedPreoffer.title}
              </h2>
              <button className="modal-close" onClick={handleCloseModal}>✕</button>
            </div>
            
            <div className="modal-body">
              {/* Image si disponible */}
              {selectedPreoffer.imageUrl && (
                <div className="offer-image-section">
                  <img 
                    src={selectedPreoffer.imageUrl} 
                    alt={selectedPreoffer.title} 
                    className="offer-detail-image"
                  />
                </div>
              )}
              
              {/* Description complète */}
              <div className="details-section">
                <h3>Description</h3>
                <p className="full-description">{selectedPreoffer.description}</p>
              </div>
              
              {/* Informations clés */}
              <div className="details-section">
                <h3>Informations</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>Catégorie</label>
                    <span>{getCategoryName(selectedPreoffer.category)}</span>
                  </div>
                  
                  {selectedPreoffer.partnerName && (
                    <div className="detail-item">
                      <label>Partenaire</label>
                      <span>{selectedPreoffer.partnerName}</span>
                    </div>
                  )}
                  
                  {selectedPreoffer.discount && (
                    <div className="detail-item">
                      <label>Avantage</label>
                      <span className="discount-highlight">{selectedPreoffer.discount}</span>
                    </div>
                  )}
                  
                  <div className="detail-item">
                    <label>Statut</label>
                    <span className={`status ${selectedPreoffer.convertedToOffer ? 'active' : 'inactive'}`}>
                      {selectedPreoffer.convertedToOffer ? '✅ Devenue offre' : '📝 En préparation'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Conditions d'utilisation */}
              {selectedPreoffer.conditions && selectedPreoffer.conditions.length > 0 && selectedPreoffer.conditions[0] !== '' && (
                <div className="details-section">
                  <h3>📋 Conditions d'utilisation</h3>
                  <ul className="conditions-list">
                    {selectedPreoffer.conditions.map((condition, index) => (
                      condition && <li key={index}>{condition}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Statistiques d'intérêt */}
              <div className="details-section">
                <h3>📊 Intérêt des entreprises</h3>
                <div className="interest-stats-detail">
                  <div className="stat-number">
                    {selectedPreoffer.interestedCompanies?.length || 0}
                  </div>
                  <div className="stat-label">
                    entreprise{selectedPreoffer.interestedCompanies?.length > 1 ? 's' : ''} intéressée{selectedPreoffer.interestedCompanies?.length > 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              {!isCompanyInterested(selectedPreoffer) && currentCompany && (
                <button 
                  className="modal-action-btn interest"
                  onClick={() => {
                    handleExpressInterest(selectedPreoffer);
                    handleCloseModal();
                  }}
                >
                  👍 Je suis intéressé
                </button>
              )}
              <button className="modal-action-btn close" onClick={handleCloseModal}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Message si non connecté */}
      {!currentCompany && preoffers.length > 0 && (
        <div className="login-warning">
          <small>⚠️ Connectez-vous pour exprimer votre intérêt</small>
        </div>
      )}
    </div>
  );
};

export default Dashboard;